import {
  faArrowLeft as ArrowLeftIcon,
  faArrowUpRightFromSquare
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Link, Stack, Tooltip, Typography } from '@mui/material';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import ExportButton from '@/components/ExportButton';
import { useFetchSettings } from '@/pages/Administration/Administration.store';
import { caseDetailsAtom } from '@/pages/Case/Case.store';
import { CASE_MANUAL_AI_STATUSES } from '@/pages/Cases';
import {
  organizationsAtom,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import isLayoutMobileAtom from '@/store/layout';
import { userIsInternalAtom } from '@/store/user.store';
import { convertTurbineLinks } from '@/utils/Common.utils';

import { CaseAISubmitDialog } from '../CaseAISubmitDialog';
import { CaseDetailsMarkdownDialog } from '../CaseDetailsMarkdownDialog';
import useStyles from './CaseSubTopbarTop.styles';

const CaseSubTopbarTop = () => {
  const navigate = useNavigate();
  const caseDetails = useAtomValue(caseDetailsAtom);
  const isLayoutMobile = useAtomValue(isLayoutMobileAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const organizations = useAtomValue(organizationsAtom);
  const [isMarkdownDialogOpen, setIsMarkdownDialogOpen] =
    useState<boolean>(false);
  const [isAISubmitDialogOpen, setIsAISubmitDialogOpen] =
    useState<boolean>(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);
  const { data: orgSettings } = useFetchSettings(
    caseDetails?.organization?.label
  );
  const { pathname } = useLocation();
  const userIsInternal = useAtomValue(userIsInternalAtom);
  const isInternalCasePage = pathname.includes('/case-internal');

  useEffect(() => {
    const checkTruncation = () => {
      const el = titleRef.current;
      if (el) {
        // For multi-line truncation, compare scrollHeight and clientHeight
        setIsTitleTruncated(el.scrollHeight > el.clientHeight + 1); // +1 for rounding
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => window.removeEventListener('resize', checkTruncation);
  }, [caseDetails?.soc_case_title, isLayoutMobile]);

  const organization =
    organizations.find((org) => org.id === caseDetails?.organization?.id) ||
    selectedOrganization;

  const { classes } = useStyles({
    isLayoutMobile
  });

  const handleBack = () => {
    navigate({
      pathname: isInternalCasePage ? '/cases-internal' : '/cases'
    });
  };

  const caseTitle = useMemo(() => {
    return (
      <Typography
        className={classes.caseTitle}
        component="h1"
        variant="h3"
        ref={titleRef}
      >
        {caseDetails?.soc_case_title}
      </Typography>
    );
  }, [caseDetails?.soc_case_title]);

  const submitToAiEnabled = useMemo(
    () =>
      selectedOrganization.id > 0 &&
      orgSettings?.ai_enable_submission &&
      CASE_MANUAL_AI_STATUSES.find(
        (status) =>
          status.toLowerCase() === caseDetails?.ai_status?.split('_').join(' ')
      ) !== undefined,
    [
      caseDetails?.ai_status,
      orgSettings?.ai_enable_submission,
      selectedOrganization.label
    ]
  );

  const exportCaseDetails = useMemo(() => {
    if (!caseDetails) return {};
    return caseDetails;
  }, [caseDetails, organization.name]);

  const turbineUrl = convertTurbineLinks(
    caseDetails?.soc_case_extra_details?.turbine_record_id as string
  );

  const isValidTurbineUrl =
    typeof turbineUrl === 'string' &&
    turbineUrl.length > 0 &&
    !turbineUrl.includes('undefined');

  return (
    <>
      <Box className={classNames(classes.caseSubTopbarTop)}>
        <Box className={classes.topContent}>
          <Stack direction="row" sx={{ alignItems: 'start' }} spacing={1}>
            <Box>
              <Typography
                variant="h6"
                style={{ display: 'inline', fontWeight: 'normal' }}
              >
                Case{' '}
                <strong>
                  {isValidTurbineUrl && userIsInternal ? (
                    <Link
                      color="primary"
                      href={turbineUrl}
                      rel="noreferrer"
                      target="_blank"
                      underline="none"
                      sx={{
                        textTransform: 'uppercase'
                      }}
                    >
                      #{caseDetails?.soc_id}
                      <FontAwesomeIcon
                        icon={faArrowUpRightFromSquare}
                        size="xs"
                        style={{ marginLeft: 4 }}
                      />
                    </Link>
                  ) : (
                    `#${caseDetails?.soc_id}`
                  )}
                </strong>
                {selectedOrganization.id !== organization?.id && (
                  <>
                    {' '}
                    for{' '}
                    <Typography
                      className={classes.topContentOrganizationName}
                      sx={{ color: 'warning.dark' }}
                    >
                      {organization.name}
                    </Typography>
                  </>
                )}
              </Typography>
              {isTitleTruncated ? (
                <Tooltip title={caseDetails?.soc_case_title}>
                  {caseTitle}
                </Tooltip>
              ) : (
                caseTitle
              )}
            </Box>
          </Stack>
          <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
            {isLayoutMobile && (
              <Button onClick={handleBack} color="primary" variant="outlined">
                <FontAwesomeIcon icon={ArrowLeftIcon} size="xl" />
              </Button>
            )}
            <ExportButton
              data={[exportCaseDetails || {}]}
              fileName={`Case ${caseDetails?.soc_id} Details`}
              json
              csv
            />
            {isInternalCasePage && (
              <>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ whiteSpace: 'nowrap' }}
                  {...(isLayoutMobile && { fullWidth: true })}
                  onClick={() => setIsMarkdownDialogOpen(true)}
                  disabled={!caseDetails?.ai_output?.ticket_text}
                >
                  Show Markdown
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ whiteSpace: 'nowrap' }}
                  {...(isLayoutMobile && { fullWidth: true })}
                  onClick={() => setIsAISubmitDialogOpen(true)}
                  disabled={!submitToAiEnabled}
                >
                  Submit to AI
                </Button>
              </>
            )}
            {/* <CaseDetailsMoreButton /> */}
          </Stack>
        </Box>
      </Box>
      {isMarkdownDialogOpen && (
        <CaseDetailsMarkdownDialog
          open={isMarkdownDialogOpen}
          setIsMarkdownDialogOpen={setIsMarkdownDialogOpen}
        />
      )}
      {isAISubmitDialogOpen && (
        <CaseAISubmitDialog
          open={isAISubmitDialogOpen}
          setOpen={setIsAISubmitDialogOpen}
        />
      )}
    </>
  );
};

export default CaseSubTopbarTop;
